import { MessageCircleQuestion, X } from "lucide-react";
import { useRef, useState } from "react";

import { AskTab } from "@/components/ask/AskTab";
import { DraggablePanel } from "@/components/reader/DraggablePanel";
import { Tip } from "@/components/ui/overlays";
import { useShell } from "@/stores/shell";
import type { TabDesc } from "@/stores/tabs";

const PANEL_WIDTH = 380;
const PANEL_HEIGHT = 480;
// The launcher FAB itself is 48px + a 16px inset from the bottom-right corner
// — reserve that plus a small gap so the open panel's own bottom-right corner
// never lands on top of the button that opened it.
const LAUNCHER_CLEARANCE = 48 + 16 + 8;

/**
 * A bottom-right launcher, always visible, that pops the AI assistant out as
 * a floating, draggable, collapsible panel (reusing `DraggablePanel`). The
 * workspace layer in `components/workspace/` can float an `ask` panel of its
 * own — `ask` is one of its seven panel types — so this is a second, simpler
 * surface beside it, not the only way to float a chat. The floating chat is a
 * self-contained, ephemeral
 * conversation — a quick-access assistant distinct from the full `ask` tabs
 * reachable from Tools → Search / the command palette, which keep their own
 * transcripts in the tab strip. Bounded to the center workspace area so it
 * can never cover the LeftBar/TopBar/RightBar chrome.
 */
export function ChatLauncher() {
  const chatOpen = useShell((s) => s.chatOpen);
  const containerRef = useRef<HTMLDivElement>(null);
  const [initial, setInitial] = useState({ x: 16, y: 16 });
  // A stable id, deliberately never registered in `stores/tabs` — this is an
  // ephemeral floating conversation, not a tab-strip entry.
  const tabRef = useRef<TabDesc>({ id: "chat-launcher", kind: "ask", title: "Assistant" });

  // DraggablePanel reads `initial` only ONCE, at mount (`useState(initial)` —
  // later prop changes are ignored). So the position must be computed and
  // committed to state BEFORE the panel's first mount, not in an effect that
  // runs after the render that flips `chatOpen` (React 18 batches both
  // `setInitial` and the store's `openChat()` from this one handler into a
  // single render, so the panel mounts already knowing where to sit).
  const openChat = () => {
    const el = containerRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      setInitial({
        x: Math.max(16, r.width - PANEL_WIDTH - 16),
        y: Math.max(16, r.height - PANEL_HEIGHT - LAUNCHER_CLEARANCE),
      });
    }
    useShell.getState().openChat();
  };

  return (
    <div ref={containerRef} className="pointer-events-none absolute inset-0 z-40">
      {chatOpen && (
        <div className="pointer-events-auto absolute inset-0">
          <DraggablePanel
            title="Assistant"
            width={PANEL_WIDTH}
            initial={initial}
            onClose={() => useShell.getState().closeChat()}
          >
            <div className="flex h-[480px] min-h-0 flex-col">
              <AskTab tab={tabRef.current} visible />
            </div>
          </DraggablePanel>
        </div>
      )}

      <Tip label={chatOpen ? "Close assistant" : "Ask the atlas"} side="left">
        <button
          type="button"
          onClick={() => (chatOpen ? useShell.getState().closeChat() : openChat())}
          aria-label={chatOpen ? "Close assistant" : "Open assistant"}
          className="pointer-events-auto absolute bottom-4 right-4 grid h-12 w-12 place-items-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105 active:scale-95"
        >
          {chatOpen ? <X className="h-5 w-5" /> : <MessageCircleQuestion className="h-5 w-5" />}
        </button>
      </Tip>
    </div>
  );
}
